import { useState, type KeyboardEvent } from 'react';
import { cn } from '../../lib/utils.js';
import { Badge } from './badge.js';

export function TagInput({
  value,
  onChange,
  placeholder = 'tag + Enter',
  validate,
  className,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  validate?: (tag: string) => string | null;
  className?: string;
}) {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function commit() {
    const cleaned = draft.trim().toLowerCase();
    if (cleaned.length === 0) return;
    if (value.includes(cleaned)) {
      setErr('Már szerepel');
      return;
    }
    const validationErr = validate?.(cleaned) ?? null;
    if (validationErr) {
      setErr(validationErr);
      return;
    }
    onChange([...value, cleaned]);
    setDraft('');
    setErr(null);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--app-divider-2)] bg-[var(--n-0)] p-2 focus-within:ring-2 focus-within:ring-[var(--brand-500)]/40">
        {value.map((tag) => (
          <Badge key={tag} tone="success" className="flex items-center gap-1">
            <span>{tag}</span>
            <button
              type="button"
              onClick={() => onChange(value.filter((t) => t !== tag))}
              className="text-primary/70 hover:text-primary"
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </Badge>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] border-0 bg-transparent text-body focus:outline-none"
        />
      </div>
      {err && <p className="text-meta text-red-700">{err}</p>}
    </div>
  );
}
